import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../../components/ui/Header';
import AuthenticationGate from '../../components/ui/AuthenticationGate';
import FeatureAccessIndicator from '../../components/ui/FeatureAccessIndicator';
import Button from '../../components/ui/Button';
import ExerciseCard from './components/ExerciseCard';
import WeeklyOverview from './components/WeeklyOverview';
import FilterControls from './components/FilterControls';
import PremiumUpgradePrompt from './components/PremiumUpgradePrompt';
import EmptyState from './components/EmptyState';
import Footer from '../../components/Footer';
import Icon from '../../components/AppIcon';
import AdSense from '../../components/AdSense';
import { useAuth } from '../../contexts/AuthContext';
import { loadAssessmentProgress } from '../../services/assessmentService';
import { loadCareerPlan } from '../../services/careerPlanService';
import { generatePersonalizedExercises, generateCategoryExercises, generateWeeklyCheckin } from '../../services/exerciseGeneratorService';
import progressTrackingService from '../../services/progressTrackingService';

const ExerciseManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userProfile, isPremium, loading: authLoading } = useAuth();
  
  const [loading, setLoading] = useState(true);

  // Exercise management state
  const [exercises, setExercises] = useState([]);
  const [filters, setFilters] = useState({
    sortBy: 'dueDate',
    status: 'all',
    duration: 'all',
    searchQuery: ''
  });
  const [isGeneratingCheckin, setIsGeneratingCheckin] = useState(false);
  const [currentWeek, setCurrentWeek] = useState(1);
  const [hasCareerPlan, setHasCareerPlan] = useState(true);
  
  // AI exercise generation state
  const [assessmentData, setAssessmentData] = useState(null);
  const [careerPlan, setCareerPlan] = useState(null);
  const [isGeneratingExercises, setIsGeneratingExercises] = useState(false);
  const [exerciseGenerationError, setExerciseGenerationError] = useState(null);
  const [hasCompletedAssessment, setHasCompletedAssessment] = useState(false);

  // Load assessment data and generate AI exercises
  useEffect(() => {
    const loadUserDataAndGenerateExercises = async () => {
      if (!user || !isPremium) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setExerciseGenerationError(null);

        // Load assessment data (optional - don't fail if it doesn't work)
        let assessmentData = null;
        let hasCompletedAssessment = false;
        
        try {
          // First check progress tracking service for assessment completion
          const progressResult = await progressTrackingService.getNavigationProgress(user.id);
          if (progressResult.success && progressResult.data?.assessmentCompleted) {
            hasCompletedAssessment = true;
            setHasCompletedAssessment(true);
            
            // Now try to load the actual assessment data
            const assessmentId = location?.state?.assessmentId;
            const assessmentResult = await loadAssessmentProgress(user.id, assessmentId);
            
            if (assessmentResult.success && assessmentResult.data) {
              assessmentData = assessmentResult.data;
              setAssessmentData(assessmentResult.data);
            }
          } else {
            setHasCompletedAssessment(false);
          }
        } catch (error) {
          console.warn('Could not load assessment data:', error);
          setHasCompletedAssessment(false);
        }

        // Load career plan if available
        try {
          const careerPlanResult = await loadCareerPlan(user.id);
          if (careerPlanResult.success && careerPlanResult.data) {
            setCareerPlan(careerPlanResult.data);
          }
        } catch (error) {
          console.warn('Could not load career plan:', error);
        }

        // Generate AI exercises only if we have assessment data
        if (hasCompletedAssessment && assessmentData) {
          await generateAIPersonalizedExercises(assessmentData);
        } else {
          // Set empty exercises if no assessment data
          setExercises([]);
        }

      } catch (error) {
        console.error('Error loading user data:', error);
        // Don't show error, just set empty state
        setHasCompletedAssessment(false);
        setExercises([]);
      } finally {
        setLoading(false);
      }
    };

    loadUserDataAndGenerateExercises();
  }, [user, isPremium]);

  // Generate AI personalized exercises
  const generateAIPersonalizedExercises = async (assessmentData) => {
    try {
      setIsGeneratingExercises(true);
      setExerciseGenerationError(null);

      const exerciseResult = await generatePersonalizedExercises(assessmentData, careerPlan);
      
      if (exerciseResult.success) {
        setExercises(exerciseResult.data);
        console.log('Generated AI exercises:', exerciseResult.data);
      } else {
        throw new Error('Failed to generate exercises');
      }
    } catch (error) {
      console.error('Error generating AI exercises:', error);
      setExerciseGenerationError('Failed to generate personalized exercises. Please try again.');
      
      // Set empty exercises if AI generation fails
      setExercises([]);
    } finally {
      setIsGeneratingExercises(false);
    }
  };

  // No mock data - all exercises are generated from real assessment data
  const mockExercises = [];

  // Initialize exercises with better error handling
  // Generate additional exercises for a specific category
  const generateCategoryExercises = async (category) => {
    if (!assessmentData) {
      console.error('No assessment data available for category generation');
      return;
    }

    try {
      setIsGeneratingExercises(true);
      const result = await generateCategoryExercises(category, assessmentData);
      
      if (result.success) {
        setExercises(prev => [...prev, ...result.data]);
      }
    } catch (error) {
      console.error('Error generating category exercises:', error);
      setExerciseGenerationError('Failed to generate additional exercises for this category.');
    } finally {
      setIsGeneratingExercises(false);
    }
  };

  // Generate weekly check-in exercise
  const generateWeeklyCheckinExercise = async () => {
    if (!assessmentData) {
      console.error('No assessment data available for weekly check-in');
      return;
    }

    try {
      setIsGeneratingCheckin(true);
      const completedExercises = exercises.filter(ex => ex.completed);
      const result = await generateWeeklyCheckin(assessmentData, completedExercises);
      
      if (result.success) {
        setExercises(prev => [result.data, ...prev]);
      }
    } catch (error) {
      console.error('Error generating weekly check-in:', error);
      setExerciseGenerationError('Failed to generate weekly check-in exercise.');
    } finally {
      setIsGeneratingCheckin(false);
    }
  };

  // Filter and sort exercises with better error handling
  const filteredExercises = useMemo(() => {
    try {
      let filtered = Array.isArray(exercises) ? [...exercises] : [];

      // Apply search filter
      if (filters?.searchQuery?.trim()) {
        const query = filters?.searchQuery?.toLowerCase();
        filtered = filtered?.filter(exercise =>
          exercise?.title?.toLowerCase()?.includes(query) ||
          exercise?.description?.toLowerCase()?.includes(query) ||
          exercise?.category?.toLowerCase()?.includes(query)
        );
      }

      // Apply status filter
      if (filters?.status !== 'all') {
        const today = new Date();
        filtered = filtered?.filter(exercise => {
          if (!exercise) return false;
          
          switch (filters?.status) {
            case 'completed':
              return exercise?.completed === true;
            case 'pending':
              return exercise?.completed !== true;
            case 'overdue':
              return exercise?.completed !== true && exercise?.dueDate && new Date(exercise.dueDate) < today;
            case 'due-today':
              return exercise?.completed !== true && exercise?.dueDate &&
                     new Date(exercise.dueDate)?.toDateString() === today?.toDateString();
            case 'due-soon':
              if (exercise?.completed === true || !exercise?.dueDate) return false;
              const diffDays = Math.ceil((new Date(exercise.dueDate) - today) / (1000 * 60 * 60 * 24));
              return diffDays > 0 && diffDays <= 2;
            default:
              return true;
          }
        });
      }

      // Apply duration filter
      if (filters?.duration !== 'all') {
        filtered = filtered?.filter(exercise => {
          if (!exercise?.estimatedMinutes) return false;
          
          switch (filters?.duration) {
            case 'short':
              return exercise?.estimatedMinutes <= 15;
            case 'medium':
              return exercise?.estimatedMinutes > 15 && exercise?.estimatedMinutes <= 30;
            case 'long':
              return exercise?.estimatedMinutes > 30;
            default:
              return true;
          }
        });
      }

      // Apply sorting
      filtered?.sort((a, b) => {
        if (!a || !b) return 0;
        
        switch (filters?.sortBy) {
          case 'title':
            return (a?.title || '')?.localeCompare(b?.title || '');
          case 'duration':
            return (a?.estimatedMinutes || 0) - (b?.estimatedMinutes || 0);
          case 'status':
            if ((a?.completed || false) !== (b?.completed || false)) {
              return (a?.completed || false) ? 1 : -1;
            }
            return new Date(a.dueDate || 0) - new Date(b.dueDate || 0);
          case 'dueDate':
          default:
            return new Date(a.dueDate || 0) - new Date(b.dueDate || 0);
        }
      });

      return filtered;
    } catch (error) {
      console.error('Error filtering exercises:', error);
      return [];
    }
  }, [exercises, filters]);

  // Handle exercise completion
  const handleCompleteExercise = (completedExercise) => {
    // Check if user is trying to exceed free tier limit
    if (!isPremium && completedExercise && !completedExercise.completed) {
      const currentUsage = getWeeklyExerciseUsage();
      if (currentUsage.used >= currentUsage.limit) {
        // Show upgrade prompt instead of completing
        return;
      }
    }

    setExercises(prev => prev?.map(exercise =>
      exercise?.id === completedExercise?.id ? completedExercise : exercise
    ));
  };

  // Handle reflection update
  const handleUpdateReflection = (exerciseId, reflection) => {
    setExercises(prev => prev?.map(exercise =>
      exercise?.id === exerciseId ? { ...exercise, reflection } : exercise
    ));
  };

  // Handle weekly check-in generation
  const handleGenerateWeeklyCheckin = async () => {
    await generateWeeklyCheckinExercise();
  };

  // Handle authentication requirement
  const handleAuthRequired = () => {
    navigate('/authentication');
  };

  // Handle logout
  const handleLogout = () => {
    setUser(null);
    // Do not use localStorage; sign out via auth context if needed
  };

  // Clear filters
  const handleClearFilters = () => {
    setFilters({
      sortBy: 'dueDate',
      status: 'all',
      duration: 'all',
      searchQuery: ''
    });
  };

  // Get exercise usage for free tier limit - only for non-premium users
  const getWeeklyExerciseUsage = () => {
    if (!user || isPremium) return { used: 0, limit: 0, remaining: 0 };
    
    const now = new Date();
    const startOfWeek = new Date(now.setDate(now.getDate() - now.getDay()));
    const endOfWeek = new Date(now.setDate(now.getDate() - now.getDay() + 6));
    
    // Count completed exercises this week (for free users)
    const completedThisWeek = Array.isArray(exercises) ? exercises.filter(ex => 
      ex && !ex.isPremium && ex.completed && ex.completedAt && 
      new Date(ex.completedAt) >= startOfWeek && 
      new Date(ex.completedAt) <= endOfWeek
    ).length : 0;
    
    const maxFreeExercises = 2;
    return {
      used: completedThisWeek,
      limit: maxFreeExercises,
      remaining: Math.max(0, maxFreeExercises - completedThisWeek)
    };
  };

  const exerciseUsage = getWeeklyExerciseUsage();
  const shouldShowUpgradePrompt = user && !isPremium && exerciseUsage.used >= exerciseUsage.limit;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-sm text-muted-foreground">Loading exercises...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Exercise Management - CareerLift</title>
        <meta name="description" content="Manage your career development exercises, track progress, and complete skill-building activities with CareerLift's comprehensive exercise system." />
      </Helmet>

      <div className="min-h-screen bg-background flex flex-col">
        <Header 
          onAuthRequired={handleAuthRequired} 
          user={user}
        />
        
        <main className="flex-1 pt-32 pb-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <AuthenticationGate user={user} requireAuth={true}>
              <div className="space-y-6">
                {/* Page Header */}
                <div className="text-center mb-2">
                  <div className="flex items-center justify-center space-x-2 mb-4">
                    <Icon name="BarChart3" size={32} className="text-primary" />
                    <h1 className="text-3xl font-bold text-foreground">
                      Exercise Management
                    </h1>
                  </div>
                  <p className="text-muted-foreground">
                    Track and complete your weekly career development exercises
                  </p>
                </div>

                {/* Page Audio */}
                <div className="flex justify-center mb-2">
                  <audio
                    src="/assets/audio/Exercise_Management.mp3"
                    controls
                    preload="metadata"
                    className="h-8 max-w-xs"
                  />
                </div>

                {/* AdSense - Banner for Free Users */}
                {!isPremium && (
                  <div className="mb-2">
                    <AdSense 
                      adSlot="1234567890"
                      adFormat="auto"
                      className="w-full"
                    />
                  </div>
                )}

                {!hasCareerPlan ? (
                  <EmptyState 
                    type="no-plan" 
                    onClearFilters={handleClearFilters} 
                  />
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                    {/* Assessment Completion Check */}
                    {!hasCompletedAssessment && (
                      <div className="lg:col-span-4">
                        <div className="bg-card border border-border rounded-lg p-8 text-center">
                          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Icon name="FileText" size={32} className="text-primary" />
                          </div>
                          <h2 className="text-2xl font-semibold text-foreground mb-3">
                            Complete Your Assessment First
                          </h2>
                          <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
                            Personalized exercises require your completed assessment to provide tailored, 
                            AI-generated activities based on your specific role, experience level, and career goals.
                          </p>
                          <div className="flex flex-col sm:flex-row gap-3 justify-center">
                            <Button
                              onClick={() => navigate('/career-assessment-setup')}
                              className="bg-primary hover:bg-primary/90"
                            >
                              <Icon name="Play" size={16} className="mr-2" />
                              Start Assessment
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => navigate('/progress-dashboard')}
                            >
                              <Icon name="BarChart3" size={16} className="mr-2" />
                              View Dashboard
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* AI Generation Status */}
                    {hasCompletedAssessment && (
                      <div className="lg:col-span-4">
                        {isGeneratingExercises && (
                          <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mb-6">
                            <div className="flex items-center space-x-3">
                              <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                              <p className="text-primary text-sm">
                                Generating personalized exercises based on your assessment...
                              </p>
                            </div>
                          </div>
                        )}

                        {exerciseGenerationError && (
                          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-6">
                            <div className="flex items-center space-x-2">
                              <Icon name="AlertCircle" size={16} className="text-destructive" />
                              <p className="text-destructive text-sm">{exerciseGenerationError}</p>
                            </div>
                          </div>
                        )}

                        {assessmentData && (
                          <div className="bg-muted/50 border border-border rounded-lg p-4 mb-6">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-3">
                                <Icon name="Bot" size={16} className="text-primary" />
                                <span className="text-sm font-medium text-foreground">
                                  AI-Generated Exercises for {assessmentData.assessment_setup?.occupation || 'Your Role'}
                                </span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <span className="text-xs text-muted-foreground">
                                  {assessmentData.assessment_setup?.seniority || 'Mid-Level'}
                                </span>
                                <div className="w-2 h-2 bg-success rounded-full"></div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Main Content */}
                    <div className="lg:col-span-3 space-y-6">
                      {/* Filter Controls */}
                      <FilterControls
                        filters={filters}
                        onFilterChange={setFilters}
                        onGenerateWeeklyCheckin={handleGenerateWeeklyCheckin}
                        exerciseCount={filteredExercises?.length}
                        isGeneratingCheckin={isGeneratingCheckin}
                      />

                      {/* Premium Upgrade Prompt - Only show for non-premium users */}
                      {shouldShowUpgradePrompt && (
                        <PremiumUpgradePrompt
                          exerciseCount={exerciseUsage.used}
                          maxFreeExercises={exerciseUsage.limit}
                        />
                      )}

                      {/* Free Tier Usage Indicator */}
                      {user && !isPremium && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                              <span className="text-sm font-medium text-blue-900">
                                Free Tier: {exerciseUsage.used}/{exerciseUsage.limit} exercises completed this week
                              </span>
                            </div>
                            <span className="text-xs text-blue-700">
                              {exerciseUsage.remaining} remaining
                            </span>
                          </div>
                          <div className="mt-2 w-full bg-blue-200 rounded-full h-2">
                            <div 
                              className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${(exerciseUsage.used / exerciseUsage.limit) * 100}%` }}
                            ></div>
                          </div>
                        </div>
                      )}

                      {/* Exercise List */}
                      {filteredExercises?.length === 0 ? (
                        <EmptyState
                          type={filters?.searchQuery || filters?.status !== 'all' || filters?.duration !== 'all' ? 'no-results' : 'no-exercises'}
                          searchQuery={filters?.searchQuery}
                          onClearFilters={handleClearFilters}
                        />
                      ) : (
                        <div className="space-y-4">
                          {filteredExercises?.map((exercise) => (
                            <ExerciseCard
                              key={exercise?.id}
                              exercise={exercise}
                              onComplete={handleCompleteExercise}
                              onUpdateReflection={handleUpdateReflection}
                              isPremium={user?.isPremium}
                            />
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Sidebar */}
                    <div className="lg:col-span-1 space-y-6">
                      <WeeklyOverview
                        exercises={exercises}
                        currentWeek={currentWeek}
                        totalWeeks={12}
                      />
                    </div>
                  </div>
                )}
              </div>
            </AuthenticationGate>
          </div>
        </main>

        {/* AdSense - Bottom Banner for Free Users */}
        {!isPremium && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-1">
            <AdSense 
              adSlot="3456789012"
              adFormat="auto"
              className="w-full"
            />
          </div>
        )}

        {/* Footer */}
        <Footer />
      </div>
    </>
  );
};

export default ExerciseManagement;